import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewContainerRef,
  OnChanges,
  SimpleChanges
} from "@angular/core";
import { Overlay, OverlayRef, ConnectedPosition } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { FormControl } from "@angular/forms";

export interface FilterItem {
  id: any;
  label: string;
}

@Component({
  selector: "app-custom-filter-multi-select-modal",
  templateUrl: "./custom-filter-multi-select-modal.component.html",
  styleUrls: ["./custom-filter-multi-select-modal.component.scss"],
  standalone: false
})
export class CustomFilterMultiSelectModalComponent implements OnChanges {
  @Input() anchor!: ElementRef<HTMLElement>;
  @Input() options: FilterItem[] = [];
  @Input() selectedItems: FilterItem[] = [];
  @Input() titleKey = "";
  @Input() placeholderText = "";
  @Input() showSearch: boolean = true;
  @Output() applyFilter = new EventEmitter<FilterItem[]>();
  @Output() clearFilter = new EventEmitter<void>();
  @ViewChild("filterTemplate") filterTemplate!: any;

  overlayRef?: OverlayRef;
  searchControl = new FormControl("");
  filteredOptions: FilterItem[] = [];
  position: any = {};

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit() {
    this.filteredOptions = [...this.options];
    this.searchControl.valueChanges.subscribe((term) => {
      const value = (term ?? "").toLowerCase();
      this.filteredOptions = this.options.filter((option) =>
        option.label.toLowerCase().includes(value)
      );
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["options"]) {
      this.filteredOptions = [...this.options];
    }

    if (changes["selectedItems"]) {
      this.selectedItems = [...(this.selectedItems || [])];
    }
  }

  open() {
    if (this.overlayRef) {
      this.close();
      return;
    }

    setTimeout(() => {
      const positions: ConnectedPosition[] = [
        {
          originX: "center",
          originY: "bottom",
          overlayX: "center",
          overlayY: "top",
          offsetY: 8
        }
      ];

      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.anchor)
        .withPositions(positions)
        .withPush(false)
        .withFlexibleDimensions(false);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: true,
        backdropClass: "cdk-overlay-transparent-backdrop",
        scrollStrategy: this.overlay.scrollStrategies.reposition()
      });

      const portal = new TemplatePortal(
        this.filterTemplate,
        this.viewContainerRef
      );
      this.overlayRef.attach(portal);

      this.overlayRef.backdropClick().subscribe(() => this.close());
    });
  }

  close() {
    this.overlayRef?.detach();
    this.overlayRef = undefined;
  }

  trackById(index: number, item: FilterItem): any {
    return item.id;
  }

  toggleSelection(item: FilterItem) {
    const index = this.selectedItems.findIndex((i) => i.id === item.id);

    if (index >= 0) {
      this.selectedItems.splice(index, 1);
    } else {
      this.selectedItems.push(item);
    }
  }

  isSelected(item: FilterItem): boolean {
    return this.selectedItems.some((i) => i.id === item.id);
  }

  onApply() {
    this.applyFilter.emit(this.selectedItems);
    this.close();
  }

  onResetSearch() {
    this.searchControl.reset();
    this.filteredOptions = [...this.options];
  }

  onClear() {
    this.selectedItems = [];
    this.applyFilter.emit(this.selectedItems);
    this.clearFilter.emit();
    this.onResetSearch();
    this.close();
  }
}
